import {
  Button,
  DialogTrigger,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
} from "react-aria-components";
import { cn } from "@core/utils";
import { Icon } from "@core/assets";
import type { SelectOption } from "./Select";

interface MultiSelectProps {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  options: readonly SelectOption[];
  /** Selected option ids. */
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  isDisabled?: boolean;
}

/**
 * Multi-select primitive: a trigger that summarises the selection, over a multi-selection
 * `ListBox` in a popover.
 *
 * Built on `DialogTrigger` + `Popover` rather than React Aria's `Select`, which is
 * single-selection only. The ListBox supplies the roving-focus and type-ahead behaviour, so
 * this stays keyboard-complete without hand-rolled key handling.
 *
 * The trigger shows the joined labels rather than "3 selected" — for short values like file
 * extensions the actual list is more useful than its length, and it matches the design's
 * "PDF, DOCX".
 */
export function MultiSelect({
  label,
  hideLabel = false,
  options,
  value,
  onChange,
  placeholder,
  description,
  errorMessage,
  isDisabled = false,
}: MultiSelectProps) {
  const invalid = Boolean(errorMessage);

  const summary = options
    .filter((option) => value.includes(option.id))
    .map((option) => option.label)
    .join(", ");

  return (
    <div className="flex w-full flex-col gap-1.5">
      <Label
        className={cn(
          "text-sm-medium text-(--text-secondary)",
          hideLabel && "sr-only"
        )}
      >
        {label}
      </Label>

      <DialogTrigger>
        <Button
          isDisabled={isDisabled}
          aria-label={label}
          className={cn(
            "flex w-full items-center gap-xs rounded-md bg-surface-field pl-3.5 pr-3.5 py-2.5 shadow-xs",
            "border text-left transition-colors outline-none",
            "focus-visible:ring-2 focus-visible:ring-focus-ring",
            "disabled:cursor-not-allowed disabled:bg-surface-field-disabled",
            invalid
              ? "border-(--border-field-error)"
              : "border-(--border-field) focus-visible:border-(--focus-ring)"
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-md-regular",
              summary === ""
                ? "text-(--text-placeholder)"
                : "text-(--text-primary)"
            )}
          >
            {summary === "" ? (placeholder ?? label) : summary}
          </span>
          <Icon
            name="chevron-down"
            size={20}
            className="shrink-0 text-(--text-tertiary)"
          />
        </Button>

        <Popover className="w-(--trigger-width) overflow-auto rounded-md border border-(--border-default) bg-surface-card py-1 shadow-lg">
          {/*
            ⚠️ Verified live: React Aria's `ListBox` treats Escape as "clear selection" on its own
            keydown handler — closing this popover with Escape (the natural way, and what any
            keyboard user reaches for) silently wiped whatever had just been picked, and this
            control commits on every change, so the wipe was already saved by the time anything
            closed. The popover's own dismiss-on-Escape is a SEPARATE, document-level listener
            (outside this React tree), so intercepting the event here in the capture phase stops
            the ListBox from ever seeing it without breaking the popover's ability to close.
          */}
          <div
            onKeyDownCapture={(event) => {
              if (event.key === "Escape") event.stopPropagation();
            }}
          >
            <ListBox
              aria-label={label}
              items={options}
              selectionMode="multiple"
              selectedKeys={new Set(value)}
              onSelectionChange={(keys) => {
                // "all" only arrives with a select-all affordance, which this control has no
                // trigger for; the Set branch is the real path.
                onChange(
                  keys === "all"
                    ? options.map((option) => option.id)
                    : [...keys].map(String)
                );
              }}
              className="outline-none"
            >
              {(option) => (
                <ListBoxItem
                  id={option.id}
                  textValue={option.label}
                  className={cn(
                    "flex cursor-pointer items-center justify-between gap-xs px-3.5 py-2.5",
                    "text-md-regular text-(--text-primary) outline-none",
                    "focus:bg-brand-50 selected:bg-brand-50 selected:text-(--text-link)"
                  )}
                >
                  {({ isSelected }) => (
                    <>
                      <span className="min-w-0 flex-1 truncate">
                        {option.label}
                      </span>
                      {isSelected ? (
                        <Icon
                          name="check"
                          size={16}
                          className="shrink-0 text-(--text-link)"
                        />
                      ) : null}
                    </>
                  )}
                </ListBoxItem>
              )}
            </ListBox>
          </div>
        </Popover>
      </DialogTrigger>

      {description ? (
        <Text
          slot="description"
          className="text-sm-regular text-(--text-tertiary)"
        >
          {description}
        </Text>
      ) : null}

      {errorMessage ? (
        <FieldError className="text-sm-regular text-(--text-error)">
          {errorMessage}
        </FieldError>
      ) : null}
    </div>
  );
}

export type { MultiSelectProps };
